
/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([], () => {
	/**
	 * @interface DashboardAPI
	 * @hideconstructor
	 * @classdesc API class that is used to control a dashboard instance behaviour.
	 */
	class DashboardAPI {
		/**
		 * @description Change the dashboard mode between <tt>EDIT</tt>, <tt>VIEW</tt> and <tt>EDIT_GROUP</tt>.
		 * @param {DashboardApi.MODES} mode Dashboard {@link DashboardApi#MODES mode} enumeration value
		 * @example
		 * DashboardApi.setMode(DashboardApi.MODES.VIEW);
		 */
		setMode() {}

		/**
		 * @function DashboardAPI#setDirty
		 * @description set a dashboard as dirty or not
		 * @param {boolean} dirty true if set a dashboard to dirty, false otherwise
		 */
		setDirty() {}

		/**
		 * @function DashboardAPI#getMode
		 * @description Get the current dashboard mode.
		 * @return {DashboardApi.MODES} Dashboard {@link DashboardApi#MODES mode} enumeration value
		 * @example
		 * DashboardApi.getMode().then(function(mode) {
		 * 	console.log(mode);
		 * });
		 */
		getMode() {}

		/**
		 * @description Get the dashboard instance specification
		 * @return {Object} dashboard spec
		 * @example
		 * DashboardApi.getSpec().then(function(spec) {
		 * 	console.log(spec);
		 * });
		 */
		getSpec() {}

		/**
		 * @function DashboardAPI#undo
		 * @description Undo the last action performed on the dashboard
		 * @example
		 * // edit the dashboard
		 * ...
		 * DashboardApi.undo();
		 */
		undo() {}

		/**
		 * @function DashboardAPI#redo
		 * @description Redo the last {@link DashboardApi#undo DashboardApi.undo} action performed on the dashboard
		 * @example
		 * // edit the dashboard
		 * ...
		 * DashboardApi.undo();
		 * DashboardApi.redo();
		 */
		redo() {}

		/**
		 * @function DashboardAPI#toggleProperties
		 * @description closes the properties panel when consumption  or group mode
		 * TODO: deprecrate/remove this method
		 */
		toggleProperties() {}

		/**
		 * @function DashboardAPI#on
		 * @description Register an event handler for dashboard events
		 * @param {string} eventName Name of the dashboard event
		 * @param {function} handler Event handler to be called when the event occurrs.
		 * Multiple handlers can be registered with the same <strong>eventName</strong>.
		 * Each handlers needs to be registered and unregistered individually.
		 * @example
		 * // handle the event when the dashboard is modified
		 * function onModified(event) {
		 * 	console.log('dashboard has been modified');
		 * }
		 *
		 * // Register the event handler
		 * DashboardApi.on(DashboardApi.EVENTS.DIRTY, onModified);
		 */
		on() {}

		/**
		 * @function DashboardAPI#off
		 * @description Unregister an event handler that was registered with {@link DashboardApi#on DashboardApi.on}
		 * @param {string} eventName Name of the dashboard event
		 * @param {function} handler Event handler to be called when the event occurrs
		 * @example
		 * // handle the event when the dashboard is modified
		 * function onModified(event) {
		 * 	console.log('dashboard has been modified');
		 * }
		 *
		 * // Register the event handler
		 * DashboardApi.on(DashboardApi.EVENTS.DIRTY, onModified);
		 * ...
		 * // Unregister the event handler
		 * DashboardApi.off(DashboardApi.EVENTS.DIRTY, onModified);
		 */
		off() {}

		/**
		 * @function DashboardAPI#addSources
		 * @description Add sources that can used as data sources in the dashboard.<br>
		 * Once the source is successfully added, it will appear on the datasource list pane with the given name.
		 * @param {String} sourceInfo.id Identifier for the source.  Will be passed back for actions such as relink
		 * @param {String} sourceInfo.name Name of the source
		 * @param {String} sourceInfo.module The module definition
		 * @example
		 * DashboardApi.addSources([{
		 *	id: 'myUniqueId',
		 *	name: 'Sample datasource',
		 *	module: {...}
		 * }, {
		 *	id: 'myUniqueId2',
		 *	name: 'Sample datasource 2',
		 *	module: {...}
		 * }]);
		 * @deprecated Use {@link DataSourcesAPI#addDataSource}
		 */
		addSources() {}

		/**
		 * @function DashboardAPI#relink
		 * @description Relink an existing source to a new module.<br>
		 * Widgets that reference this source will reload after the relink is finished.
		 * @param {String} sourceInfo.id Id of the source to relink.  This id is passed to the caller in the relink:clicked event
		 * @param {String} sourceInfo.name Name of the source, only needed if you wish to change the name.
		 * @param {String} sourceInfo.module The module definition
		 * @example
		 * DashboardApi.relink({
		 *	id: '123456',
		 *	name: 'Sample datasource',
		 *	module: {...}
		 * });
		 * @deprecated Use {@link DataSourceAPI#relink}
		 */
		relink() {}

		/**
		 * @function DashboardAPI#getCanvas
		 * @description Get the {@link CanvasAPI Canvas} API
		 * @return {CanvasAPI}
		 */
		getCanvas() {}

		/**
		 * @async
		 * @function DashboardAPI#getCanvasWhenReady
		 * @description Asynchronously get the {@link CanvasAPI Canvas} API
		 * @return {Promoise<CanvasAPI>}
		 */
		getCanvasWhenReady() {}

		/**
		 * @function DashboardAPI#getType
		 * @description Get the dashboard type
		 * @return {string} dashboard type ['dashboard' | 'story' | 'explore']
		 */
		getType() {}

		/**
		 * @function DashboardAPI#getName
		 * @description Get the dashboard name
		 * @return {string} dashboard name
		 */
		getName() {}

		/**
		 * @function DashboardAPI#canAuthor
		 * @description Determine whether user has permission to author the dashboard
		 * @return {boolean} true if the dashboard can be authored, otherwise false
		 */
		canAuthor() {}

		/**
		 * @function DashboardAPI#getService
		 * @description Get a global service
		 * @param {DashboardAPI.GLOBAL_SERVICES} service global service enum
		 * @return {object} service object
		 */
		getService( /* service */ ) {}

		/**
		 * @async
		 * @function DashboardAPI#getServiceWhenReady
		 * @description Asynchronously get the global service
		 * @param {DashboardAPI.GLOBAL_SERVICES} service global service enum
		 * @return {Promise<object>} Promise which resolves a service object
		 */
		getServiceWhenReady( /* service */ ) {}

		/**
		 * @function DashboardAPI#getFeature
		 * @description Get the feature with the given feature name
		 * @param {string} name unique name of the feature
		 * @return {object} the feature API object
		 */
		getFeature( /* name */ ) {}

		/**
		 * @function DashboardAPI#triggerDashboardEvent
		 * @description Trigger a dashboard event
		 * @param {string} name Event name
		 * @param {object} payload Event payload
		 */
		triggerDashboardEvent( /* name, payload */ ) {}

		/**
		 * @function DashboardAPI#showToast
		 * @description Display a toast message
		 * @param {string} message the message string
		 * @param {object} options toast message options
		 * @param {string} options.type message type ('info', 'error')
		 * @param {boolean} options.preventDuplicates prevent from duplicate toast messages being displayed
		 * @deprecated replaced by Glass feature showToast
		 */
		showToast( /* message, options */ ) {}

		/**
		 * @function DashboardAPI#showMessage
		 * @description Display a message box with the given message
		 * @param {string} message the message string
		 * @param {string} [type] message type ('info', 'error')
		 */
		showMessage( /* message, type = 'info' */ ) {}

		/**
		 * @function DashboardAPI#showSlideOut
		 * @description Display a slideout on the dashboard
		 * @param {object} option slideout options
		 */
		showSlideOut( /* options */ ) {}

		/**
		 * @function DashboardAPI#showContextMenu
		 * @description Display a context menu on the dashboard
		 * @param {object} option Context menu options
		 */
		showContextMenu( /* options */ ) {}

		/**
		 * @function DashboardAPI#getPluginExtension
		 * @description get the plugin extension object
		 * @param {string} id unique plugin identifier
		 */
		getPluginExtension( /* id */ ) {}

		/**
		 * @function DashboardAPI#getCollectionExtension
		 * @description get the collection extension object
		 * @param {string} id unique collection identifier
		 */
		getCollectionExtension() {}

		/**
		 * @function DashboardAPI#getConfiguration
		 */
		getConfiguration() {}

		/**
		 * @function DashboardAPI#getAppConfig
		 * @deprecated replaced by DashboardAPI#getConfiguration
		 */
		getAppConfig() {}

		/**
		 * @function DashboardAPI#openApplication
		 * @param {string} name application name
		 * @param {object} options application options
		 */
		openApplication( /* name, options */ ) {}

		/**
		 * @function DashboardAPI#closeApplication
		 * @param {string} name application name
		 * @param {string} id application unique identifier
		 * @param {object} options application options
		 */
		closeApplication( /* name, id, options */ ) {}

		/**
		 * @function DashboardAPI#showErrorMessage
		 * @deprecated replaced by DashboardAPI#showMessage
		 */
		showErrorMessage() {}

		/**
		 * @function DashboardAPI#getApplicationName
		 * @deprecated replaced by DashboardAPI#getType
		 */
		getApplicationName() {}

		/**
		 * @function DashboardAPI#getApplicationLabel
		 * @deprecated replaced by DashboardAPI#getName
		 */
		getApplicationLabel() {}

		/**
		 * @function DashboardAPI#getGlassSvc
		 * @deprecated replaced by DashboardAPI#getServiceWhenReady
		 */
		getGlassSvc() {}

		/**
		 * @function DashboardAPI#getGlassCoreSvc
		 * @deprecated replaced by DashboardAPI#getService
		 */
		getGlassCoreSvc() {}

		/**
		 * @function DashboardAPI#getDashboardSvc
		 * @deprecated replaced by {@link DashboardAPI#getFeature}
		 */
		getDashboardSvc() {}

		/**
		 * @function DashboardAPI#getDashboardSvc
		 * @deprecated replaced by {@link DashboardAPI#getFeature}
		 */
		getDashboardCoreSvc() {}

		/**
		 * @function DashboardAPI#findGlassPlugin
		 * @deprecated replaced by {@link DashboardAPI#getPluginExtension}
		 */
		findGlassPlugin() {}

		/**
		 * @function DashboardAPI#findGlassCollection
		 * @deprecated replaced by {@link DashboardAPI#getCollectionExtension}
		 */
		findGlassCollection() {}

		/**
		 * @function DashboardAPI#getLayout
		 * @deprecated already exists in {@link CanvasAPI}
		 * @todo confirm this is not being used by explore
		 */
		getLayout() {}

		/**
		 * @function DashboardAPI#deselectAllWidgets
		 * @deprecated moved to {@link CanvasAPI}
		 * Delect all currently select widgets
		 */
		deselectAllWidgets() {}

		/**
		 * @function DashboardAPI#getToolbarActions
		 * @deprecated
		 */
		getToolbarActions() {}

		/**
		 * @function DashboardAPI#openAppView
		 * @deprecated Replaced by {@link DashboardAPI#openGlassApplication}
		 */
		openAppView() {}

		/**
		 * @function DashboardAPI#addContentToCanvas
		 * @description Add a content to the current dashboard canvas
		 * @deprecated Replaced by {@link CanvasAPI#addContent}
		 */
		addContentToCanvas() {}

		/**
		 * @function DashboardAPI#getCollectionConfig
		 * @deprecated needs to be integrated as part of {@link DashboardAPI#getCollectionExtension}
		 */
		getCollectionConfig() {}

		/**
		 * @todo DO NOT document. Needs to be removed.
		 * @deprecated this should not be exposed as an API
		 * @ignore
		 */
		getCurrentContentView() {}

		/**
		 * @function DashboardAPI#getSources
		 * @description Gets the data sources that are in use in the dashboard.<br>
		 * Once the source is successfully added, it will appear on the datasource list pane with the given name.
		 * Returns a promise that resolves to an array of datasource info:
		 * 		[{ name: <localized name of datasource>,
		 * 		   assetId: <storeId of datasource>,
		 * 		   type: <datasource type e.g. uploadedFile>}],
		 *		   active: <true if the datasource is used in selected widgets or selected in datasource panel>}],
		 * @example
		 * DashboardApi.getSources();
		 * @deprecated Use{@link DataSourcesAPI#getSourcesInfo} instead
		 */
		getSources() {}

		/**
		 * @function DashboardAPI#getDataSourceList
		 * @description Gets the data sources that are being used by the dashboard.
		 * @return {Promise<DataSourceAPI[]>}  Returns a promise that will be resolved with an array of {@link DataSourceAPI} objects.
		 * @deprecated Use {@link DashboardAPI#getFeature} to get the dataSource feature and call {@link DataSourcesAPI#getDataSourceList}
		 */
		getDataSourceList() {}

		/**
		 * @function DashboardAPI#setActiveDataSourceId
		 * @description Keep track of the current active datasource with the given datasource id
		 * @param {string} sourceId current active datasource identifier
		 * @deprecated Use {@link DataSourcesAPI#setActiveDataSourceId} instead
		 */
		setActiveDataSourceId() { }

		/**
		 * @function DashboardAPI#getActiveDataSourceId
		 * @description Get the current active datasource id
		 * @return {string} current active datasource identifier
		 * @deprecated Use {@link DataSourcesAPI#getActiveDataSourceId} instead
		 */
		getActiveDataSourceId() { }

		/**
		 * @deprecated Moved to {@link CanvasAPI Canvas} API
		 * @function DashboardAPI#copy
		 * @description Copy the currently selected widget(s) on the dashboard
		 */
		copy() {}

		/**
		 * @deprecated Moved to {@link CanvasAPI Canvas} API
		 * @function DashboardAPI#paste
		 * @description Paste the copied widget(s) to the dashboard
		 * @return {Promise} promise which is resolved once the widget(s) are successfully pasted
		 */
		paste() {}

		destroy() {}
	}


	/**
	 * @public
	 * @readonly
	 * @enum {string}
	 * @memberof DashboardAPI
	 */
	DashboardAPI.MODES = {
		/** Edit (authoring) mode.<br>
		Allows user to author the current dashboard. */
		EDIT: 'authoring',
		/** View (consumption) mode.<br>
		Allows user to view and explore the current dashboard. */
		VIEW: 'consumption',
		/** Event group edit mode.<br>
		Allows user to edit the widget connections in the current dashboard. */
		EDIT_GROUP: 'eventGroups'
	};

	/**
	 * @public
	 * @readonly
	 * @enum {string}
	 * @memberof DashboardAPI
	 */
	DashboardAPI.GLOBAL_SERVICES = {
		/** AJAX service */
		AJAX: '.Ajax',
		/** Logger service */
		LOGGER: '.Logger',
		/** LiveWidget service */
		LIVEWIDGET: '.LiveWidgetService'
	};

	/**
	 * @public
	 * @readonly
	 * @enum {string}
	 * @memberof DashboardAPI
	 */
	DashboardAPI.GLOBAL_INTERNAL_SERVICES = {
		CONTENT: '.Content',
		USER_PROFILE: '.UserProfile',
		PINNING: '.DashboardPinning',
		THEME: '.DashboardTheme',
		CONVERSION: '.ConversionService',
		DRILL_INFO: '.DrillInfoService',
		CONTENT_DIALOG: '.ContentDialogFactory',
		DATA_CONNECTION: '.DataConnectionServiceFactory'
	};

	return DashboardAPI;
});
